import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Line, Area, ReferenceLine, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getApiUrl } from '../App';
import { getToken } from '../context/AuthContext';

const BB_PERIOD = 20;

function computeBB(prices) {
  return prices.map((point, i) => {
    const slice = prices.slice(Math.max(0, i - BB_PERIOD + 1), i + 1);
    const closes = slice.map(p => p.close);
    const mean = closes.reduce((a, b) => a + b, 0) / closes.length;
    const variance = closes.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / closes.length;
    const stdDev = Math.sqrt(variance);
    return {
      ...point,
      sma: parseFloat(mean.toFixed(2)),
      upper: parseFloat((mean + 2 * stdDev).toFixed(2)),
      lower: parseFloat((mean - 2 * stdDev).toFixed(2)),
      band: [parseFloat((mean - 2 * stdDev).toFixed(2)), parseFloat((mean + 2 * stdDev).toFixed(2))],
    };
  });
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const SIGNAL_CONFIGS = {
  bear_call_spread: { label: 'Bear Call Spread', color: '#c0392b', bg: '#fdf2f2', border: '#e74c3c' },
  dissuaded_bear:   { label: 'Avoid Bear Play',  color: '#8e44ad', bg: '#f9f0ff', border: '#9b59b6' },
  covered_call:     { label: 'Covered Call',      color: '#e67e22', bg: '#fef9f0', border: '#f39c12' },
  cash_secured_put: { label: 'Cash Secured Put',  color: '#27ae60', bg: '#f0faf4', border: '#2ecc71' },
  neutral:          { label: 'No Strong Signal',  color: '#7f8c8d', bg: '#f8f9fa', border: '#bdc3c7' },
  straddle_strangle:{ label: 'Long Straddle / Strangle', color: '#2980b9', bg: '#eaf4fd', border: '#3498db' },
  none:             { label: 'No Signal',         color: '#bdc3c7', bg: '#f8f9fa', border: '#ecf0f1' },
};

const VALUATION_CONFIGS = {
  overvalued:    { color: '#e74c3c', bg: '#fdf2f2', label: 'Overvalued' },
  undervalued:   { color: '#27ae60', bg: '#f0faf4', label: 'Undervalued' },
  fairly_valued: { color: '#f39c12', bg: '#fef9f0', label: 'Fairly Valued' },
  unknown:       { color: '#bdc3c7', bg: '#f8f9fa', label: 'No Valuation Data' },
};

function SectionHeader({ num, title }) {
  return (
    <h3 style={{ fontSize: '15px', color: '#2c3e50', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        background: '#2c3e50', color: 'white', borderRadius: '50%',
        width: '22px', height: '22px', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0,
      }}>{num}</span>
      {title}
    </h3>
  );
}

function SignalBadge({ strength, label, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
      {strength !== 'none' && (
        <span style={{
          background: color, color: 'white', padding: '4px 12px',
          borderRadius: '20px', fontSize: '12px', fontWeight: '700',
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          {strength} signal
        </span>
      )}
      <span style={{ fontSize: '16px', fontWeight: '600', color }}>{label}</span>
    </div>
  );
}

function ValuationBadge({ valuation }) {
  if (!valuation || valuation.status === 'unknown') return null;
  const cfg = VALUATION_CONFIGS[valuation.status] || VALUATION_CONFIGS.unknown;
  const sign = valuation.distanceFromFairValue > 0 ? '+' : '';
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`,
      borderRadius: '12px', padding: '3px 10px', fontSize: '12px', fontWeight: '600',
    }}>
      {cfg.label}
      {valuation.fairValue ? ` · ${sign}${valuation.distanceFromFairValue}% vs $${valuation.fairValue.toFixed(2)}` : ''}
    </span>
  );
}

const OptionsAnalysis = ({ symbol }) => {
  const [analysis, setAnalysis] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(getApiUrl(`/api/options-analysis/${symbol}`), {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then(r => {
        if (!r.ok) throw new Error('Failed to fetch options analysis');
        return r.json();
      }),
      fetch(getApiUrl(`/api/historical-prices/${symbol}?timeframe=3months`)).then(r => {
        if (!r.ok) throw new Error('Failed to fetch price history');
        return r.json();
      }),
    ])
      .then(([analysisData, histData]) => {
        setAnalysis(analysisData);
        const prices = (histData.prices || [])
          .map(p => ({ date: formatDate(p.date), close: parseFloat(p.close) }))
          .reverse();
        setChartData(computeBB(prices));
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) return <div className="loading-spinner">Loading options analysis...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!analysis) return null;

  const { currentPrice, bollingerBands, valuation, volatility, signals, cspYield, coveredCallYield } = analysis;
  const { sma, upperBand, lowerBand, percentB } = bollingerBands;

  const mrSignal = signals?.meanReversion || {};
  const vsSignal = signals?.volatilitySqueeze || {};
  const mrCfg = SIGNAL_CONFIGS[mrSignal.signal] || SIGNAL_CONFIGS.neutral;
  const vsCfg = SIGNAL_CONFIGS[vsSignal.signal] || SIGNAL_CONFIGS.none;

  const indicatorPct = Math.min(Math.max(percentB * 100, 0), 100);

  return (
    <div className="options-analysis">
      <h2>Options Analysis — {symbol}</h2>
      <p className="options-subtitle">20-day Bollinger Bands (2σ) + Fundamental Valuation</p>

      {/* BB Chart */}
      <div className="bb-chart-container" style={{ marginBottom: '28px' }}>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis domain={['auto', 'auto']} tickFormatter={v => `$${v}`} tick={{ fontSize: 11 }} width={60} />
            <Tooltip formatter={(value, name) => {
              if (Array.isArray(value)) return null;
              return [`$${Number(value).toFixed(2)}`, name];
            }} />
            <Legend />
            <Area type="monotone" dataKey="band" name="BB Range" stroke="none" fill="#3498db" fillOpacity={0.08} legendType="none" />
            <Line type="monotone" dataKey="upper" name="Upper Band" stroke="#e74c3c" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
            <Line type="monotone" dataKey="sma" name="SMA 20" stroke="#e67e22" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
            <Line type="monotone" dataKey="lower" name="Lower Band" stroke="#27ae60" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
            <Line type="monotone" dataKey="close" name="Price" stroke="#2c3e50" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
            {valuation?.fairValue && (
              <ReferenceLine
                y={valuation.fairValue}
                stroke="#8e44ad"
                strokeDasharray="6 3"
                strokeWidth={1.5}
                label={{ value: `Fair Value $${valuation.fairValue.toFixed(2)}`, position: 'right', fill: '#8e44ad', fontSize: 11 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Band position gauge */}
      <div className="bb-gauge" style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
          <span>Lower <strong style={{ color: '#27ae60' }}>${lowerBand.toFixed(2)}</strong></span>
          <span>SMA <strong>${sma.toFixed(2)}</strong></span>
          <span>Upper <strong style={{ color: '#e74c3c' }}>${upperBand.toFixed(2)}</strong></span>
        </div>
        <div style={{
          position: 'relative', height: '36px', borderRadius: '6px',
          background: 'linear-gradient(to right, #27ae60, #f8f9fa 40%, #f8f9fa 60%, #e74c3c)',
          border: '1px solid #ddd',
        }}>
          <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', background: '#bdc3c7', transform: 'translateX(-50%)' }} />
          <div style={{
            position: 'absolute', left: `${indicatorPct}%`, top: '50%',
            transform: 'translate(-50%, -50%)', background: '#2c3e50', color: 'white',
            padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '700',
            whiteSpace: 'nowrap', zIndex: 1,
          }}>
            ${currentPrice.toFixed(2)}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap', gap: '6px' }}>
          <span style={{ fontSize: '13px', color: '#888' }}>
            %B: <strong>{(percentB * 100).toFixed(1)}%</strong>
            &nbsp;·&nbsp;
            {percentB >= 0.8 ? 'Near upper band' : percentB <= 0.2 ? 'Near lower band' : 'Mid-range'}
          </span>
          <ValuationBadge valuation={valuation} />
        </div>
      </div>

      {/* ── Engine 1: Mean Reversion ── */}
      <div style={{ marginBottom: '20px' }}>
        <SectionHeader num="1" title="Mean Reversion Engine" />
        <div style={{ background: mrCfg.bg, border: `2px solid ${mrCfg.border}`, borderRadius: '8px', padding: '16px' }}>
          <SignalBadge strength={mrSignal.strength} label={mrCfg.label} color={mrCfg.color} />
          <p style={{ margin: 0, color: '#555', lineHeight: '1.6', fontSize: '14px' }}>{mrSignal.reasoning}</p>
        </div>
      </div>

      {/* ── Engine 2: Volatility Squeeze ── */}
      <div style={{ marginBottom: '20px' }}>
        <SectionHeader num="2" title="Volatility Squeeze / Expansion" />
        <div style={{
          background: volatility?.isSqueeze ? '#eaf4fd' : '#f8f9fa',
          border: `2px solid ${volatility?.isSqueeze ? '#3498db' : '#ddd'}`,
          borderRadius: '8px', padding: '16px',
        }}>
          {volatility ? (
            <>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888', marginBottom: '5px' }}>
                  <span>Narrowest (12-mo)</span>
                  <span>BB Width — <strong>{(volatility.bbWidthPercentile * 100).toFixed(0)}th percentile</strong></span>
                  <span>Widest (12-mo)</span>
                </div>
                <div style={{ position: 'relative', height: '12px', background: '#ecf0f1', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${volatility.bbWidthPercentile * 100}%`,
                    background: volatility.isSqueeze ? '#3498db' : '#95a5a6',
                    borderRadius: '6px',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
                  <span>${volatility.twelveMonthLowWidth.toFixed(2)}</span>
                  <span>Current: ${volatility.currentWidth.toFixed(2)}</span>
                  <span>${volatility.twelveMonthHighWidth.toFixed(2)}</span>
                </div>
              </div>

              {volatility.isSqueeze ? (
                <>
                  <SignalBadge strength={vsSignal.strength} label={vsCfg.label} color={vsCfg.color} />
                  <p style={{ margin: 0, color: '#555', fontSize: '14px', lineHeight: '1.6' }}>{vsSignal.reasoning}</p>
                </>
              ) : (
                <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
                  Bands are at the <strong>{(volatility.bbWidthPercentile * 100).toFixed(0)}th percentile</strong> of 12-month width.
                  No compression detected — volatility expansion strategies (straddles / strangles) are not favored here.
                </p>
              )}

              <div style={{ marginTop: '10px', fontSize: '12px', color: '#aaa' }}>
                Historical vol (30-day annualized): <strong>{(volatility.historicalVol * 100).toFixed(1)}%</strong>
              </div>
            </>
          ) : (
            <p style={{ margin: 0, color: '#aaa', fontSize: '14px' }}>Insufficient price history for squeeze analysis.</p>
          )}
        </div>
      </div>

      {/* ── Engine 3: CSP Yield-to-Value ── */}
      <div style={{ marginBottom: '20px' }}>
        <SectionHeader num="3" title="Income Strategy — CSP Yield-to-Value" />
        <div style={{ background: '#f0faf4', border: '2px solid #2ecc71', borderRadius: '8px', padding: '16px' }}>
          <p style={{ margin: '0 0 14px', fontSize: '14px', color: '#555', lineHeight: '1.5' }}>
            Sell a Cash Secured Put at the <strong>Lower Band (${lowerBand.toFixed(2)})</strong> to enter near a statistical floor.
            {valuation?.status === 'undervalued' && (
              <> The stock appears <strong style={{ color: '#27ae60' }}>undervalued</strong>, adding fundamental support to this level.</>
            )}
          </p>

          {cspYield && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                {[
                  { label: '30-Day DTE', premium: cspYield.estimatedPremium30d, yield: cspYield.annualizedYield30d },
                  { label: '45-Day DTE', premium: cspYield.estimatedPremium45d, yield: cspYield.annualizedYield45d },
                ].map(({ label, premium, yield: y }) => (
                  <div key={label} style={{ background: 'white', borderRadius: '6px', padding: '12px', border: '1px solid #d5f5e3' }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#27ae60' }}>
                      {y}% <span style={{ fontSize: '12px', fontWeight: '400', color: '#888' }}>annualized</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>Est. premium: ${premium.toFixed(2)} / share</div>
                  </div>
                ))}
              </div>

              {cspYield.yieldToFairValue && valuation?.fairValue && (
                <div style={{ background: '#eafaf1', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#1e8449', marginBottom: '10px' }}>
                  <strong>Yield-to-Fair-Value:</strong> If assigned at ${lowerBand.toFixed(2)} and stock recovers to
                  fair value (${valuation.fairValue.toFixed(2)}), total return = <strong>{cspYield.yieldToFairValue.toFixed(1)}%</strong>
                </div>
              )}
            </>
          )}

          <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>
            Premiums estimated using {volatility ? `${(volatility.historicalVol * 100).toFixed(1)}%` : '—'} historical vol via Black-Scholes.
            Actual IV and market prices will differ.
          </p>
        </div>
      </div>

      {/* ── Engine 4: Covered Call Income ── */}
      <div style={{ marginBottom: '20px' }}>
        <SectionHeader num="4" title="Income Strategy — Covered Call Yield" />
        <div style={{ background: '#fef9f0', border: '2px solid #f39c12', borderRadius: '8px', padding: '16px' }}>
          <p style={{ margin: '0 0 14px', fontSize: '14px', color: '#555', lineHeight: '1.5' }}>
            Sell a Covered Call at the <strong>Upper Band (${upperBand.toFixed(2)})</strong> to collect premium near a statistical ceiling.
            {valuation?.status === 'overvalued' && (
              <> The stock appears <strong style={{ color: '#e74c3c' }}>overvalued</strong>, adding fundamental conviction that price is unlikely to run past this level.</>
            )}
            {coveredCallYield?.strikeAboveFairValue === false && (
              <> <strong style={{ color: '#8e44ad' }}>Note:</strong> The upper band strike is below fair value (${valuation?.fairValue?.toFixed(2)}) — you risk being called away before the stock reaches fair value.</>
            )}
          </p>

          {coveredCallYield && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                {[
                  { label: '30-Day DTE', premium: coveredCallYield.estimatedPremium30d, yield: coveredCallYield.annualizedYield30d },
                  { label: '45-Day DTE', premium: coveredCallYield.estimatedPremium45d, yield: coveredCallYield.annualizedYield45d },
                ].map(({ label, premium, yield: y }) => (
                  <div key={label} style={{ background: 'white', borderRadius: '6px', padding: '12px', border: '1px solid #fde8c0' }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#e67e22' }}>
                      {y}% <span style={{ fontSize: '12px', fontWeight: '400', color: '#888' }}>annualized</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>Est. premium: ${premium.toFixed(2)} / share</div>
                  </div>
                ))}
              </div>

              {coveredCallYield.yieldIfCalled !== null && (
                <div style={{ background: '#fef5e4', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#9a5e0a', marginBottom: '10px' }}>
                  <strong>Yield if Called Away:</strong> If stock reaches the upper band (${upperBand.toFixed(2)}) and shares are called,
                  total return = <strong>{coveredCallYield.yieldIfCalled.toFixed(1)}%</strong> (premium + capital gain to strike)
                </div>
              )}
            </>
          )}

          <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>
            Premiums estimated using {volatility ? `${(volatility.historicalVol * 100).toFixed(1)}%` : '—'} historical vol via Black-Scholes.
            Actual IV and market prices will differ.
          </p>
        </div>
      </div>

      {/* Raw values */}
      <details style={{ marginTop: '8px' }}>
        <summary style={{ cursor: 'pointer', color: '#888', fontSize: '13px' }}>Raw values</summary>
        <table style={{ marginTop: '10px', width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <tbody>
            {[
              ['Period', `${bollingerBands.period} days`],
              ['Current Price', `$${currentPrice.toFixed(2)}`],
              ['Upper Band (2σ)', `$${upperBand.toFixed(2)}`],
              ['SMA (20-day)', `$${sma.toFixed(2)}`],
              ['Lower Band (2σ)', `$${lowerBand.toFixed(2)}`],
              ['Std Deviation', `$${bollingerBands.stdDev.toFixed(2)}`],
              ['%B', `${(percentB * 100).toFixed(2)}%`],
              ...(valuation?.fairValue ? [
                ['Fair Value (Graham)', `$${valuation.fairValue.toFixed(2)}`],
                ['Distance from Fair Value', `${valuation.distanceFromFairValue > 0 ? '+' : ''}${valuation.distanceFromFairValue}%`],
                ['Actual Growth (3yr CAGR)', `${valuation.actualGrowth?.toFixed(1)}%`],
                ['Fair P/E', `${valuation.fairPE?.toFixed(1)}x`],
                ['Latest EPS', `$${valuation.latestEPS?.toFixed(2)}`],
              ] : []),
              ...(volatility ? [
                ['BB Width Percentile', `${(volatility.bbWidthPercentile * 100).toFixed(0)}th`],
                ['Historical Vol (30d)', `${(volatility.historicalVol * 100).toFixed(1)}%`],
                ['12-mo BB Width Range', `$${volatility.twelveMonthLowWidth.toFixed(2)} – $${volatility.twelveMonthHighWidth.toFixed(2)}`],
              ] : []),
            ].map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 8px', color: '#666' }}>{label}</td>
                <td style={{ padding: '6px 8px', fontWeight: '600' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};

export default OptionsAnalysis;
